import { AxiosError } from 'axios';
import { toast } from 'sonner';

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  errors?: Record<string, string[]>;
}

export class ErrorHandler {
  static handle(error: unknown): ApiError {
    if (error instanceof AxiosError) {
      return this.handleAxiosError(error);
    }

    if (error instanceof Error) {
      return {
        message: error.message,
      };
    }

    return {
      message: 'An unexpected error occurred',
    };
  }

  private static handleAxiosError(error: AxiosError): ApiError {
    const { response } = error;

    if (!response) {
      return {
        message: 'Network error. Please check your connection.',
      };
    }

    const data = response.data as Record<string, unknown>;

    // Extract error message from nested error object or top-level message
    const errorData = data?.error as Record<string, unknown> | undefined;
    const message = (errorData?.message as string) || (data?.message as string) || 'An error occurred';

    return {
      message,
      code: (data?.code as string) || undefined,
      status: response.status,
      errors: (data?.errors as Record<string, string[]>) || undefined,
    };
  }

  static toast(error: unknown, fallbackMessage?: string): void {
    const apiError = this.handle(error);
    toast.error(apiError.message || fallbackMessage || 'An error occurred');
  }

  static getValidationErrors(error: ApiError): Record<string, string> {
    if (!error.errors) return {};

    const errors: Record<string, string> = {};

    Object.entries(error.errors).forEach(([field, messages]) => {
      errors[field] = messages[0]; // Take first error message
    });

    return errors;
  }
}
